import {Trans, useTranslation} from "react-i18next";
import {Link, useNavigate} from "react-router-dom";
import React, {useEffect, useState} from "react";
import {auth} from "../firebase";
import {
    getMultiFactorResolver,
    multiFactor,
    PhoneAuthProvider,
    PhoneMultiFactorGenerator,
    RecaptchaVerifier,
    signInWithEmailAndPassword,
    signOut
} from "firebase/auth";
import {RegisterContent, RegisterFooter, RegisterHeader, RegisterInstead} from "./Register";
import {useInterval} from "../utils";
import {TextField} from "../components/textField";

let recaptchaWidgetId = null,
    recaptchaVerifier = null

function initRecaptcha() {
    if (!recaptchaVerifier) {
        recaptchaVerifier = new RecaptchaVerifier(auth, "request-otp", {
            'size': "invisible",
        })
        recaptchaVerifier.render().then((widgetId) => {
            recaptchaWidgetId = widgetId;
        });
    }
}

export function Enroll2FA() {
    const {t} = useTranslation("register");
    const navigate = useNavigate();
    const [error, setError] = useState(null);
    const [view, setView] = useState("enable-2fa") // "enable-2fa" | "confirm-2fa"
    const [fetching, setFetching] = useState(false);
    const [verificationId, setVerificationId] = useState('');
    const [phone, setPhone] = useState('');
    const [hasVerified, setHasVerified] = useState(false);
    //const [selectedCountryCode, setSelectedCountryCode] = useState(countryPhoneCodes.find(c => c.code === "SI"));
    //const [query, setQuery] = useState('')

    useEffect(() => {
        if (!auth.currentUser) {
            navigate("/")
            return
        }
        setError(null)
        switch (view) {
            case "enable-2fa":
                initRecaptcha()
                break;
            case "confirm-2fa":
                //setTimeout(60)
                break;
            default:
                setView("enable-2fa")
        }
    }, [view])

    async function verifyPhoneNumber() {
        if (!(auth.currentUser && phone)) {
            navigate("/")
            return
        }

        const session = await multiFactor(auth.currentUser).getSession().catch(err => {
            setError("There was an error while trying to get the session. Please try again later")
            return null
        })

        if (!session) {
            navigate("/")
            return
        }

        function _callback() {
            if (hasVerified) return
            if (!phone || !session) return
            const phoneInfoOptions = {
                phoneNumber: phone,
                session
            }

            setError(null)
            setHasVerified(true)

            if (!window._phoneAuthProvider) window._phoneAuthProvider = new PhoneAuthProvider(auth);
            return window._phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier).then(verificationId => {
                setFetching(false)
                setVerificationId(verificationId)
                setHasVerified(true)
                return verificationId
            }).catch(err => {
                console.error("verifyPhoneNumber error:", err)
                if (err.code === "auth/requires-recent-login") {
                    logout()
                }
                setFetching(false)
                setError(err.message)
                setHasVerified(false)
            })
        }

        return _callback()
    }

    function logout() {
        signOut(auth).finally(() => window.location.reload())
    }

    const phoneNumberRegex = /^\+\d{1,3}\d{10}$/;

    function onEnable2FASubmit(e) {
        e.preventDefault();

        if (!phone) {
            setError(t("pleaseEnterAValidPhoneNumber"))
            return
        }

        const _phone = phone.replace(/\s/g, '').replace(/-/g, '')

        if (!_phone.startsWith("+") && !_phone.startsWith("00")) {
            setError(t("pleaseEnterAValidPhoneNumber"))
            return
        }

        if (_phone.match(phoneNumberRegex)) {
            setPhone(_phone)
            setView("confirm-2fa")
        } else {
            setError(t("pleaseEnterAValidPhoneNumber"))
        }
    }

    async function onConfirm2FASubmit(code) {
        if (!verificationId || !auth.currentUser) {
            setError(t('somethingWentWrongPleaseRefreshThePageAndTryAgain'))
            return
        }

        // Ask user for the verification code. Then:
        const cred = PhoneAuthProvider.credential(verificationId, code),
            multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);

        setFetching(true)


        multiFactor(auth.currentUser).enroll(multiFactorAssertion, "Default").then(() => {
            setFetching(false)
            setError(null)
            navigate("/u/dashboard", {replace: true})
        }).catch(err => {
            setFetching(false)
            console.error(err)
            if (err?.message) {
                setError(err.message)
            } else {
                setError(t('somethingWentWrongPleaseRefreshThePageAndTryAgain'))
            }
        })
    }

    return (
        <div className="h-full flex flex-col">
            <RegisterHeader/>
            <RegisterContent>
                <div className="m-auto px-8 sm:px-16 md:px-0">
                    {view === "enable-2fa" && (
                        <form onSubmit={onEnable2FASubmit}>

                            <h1 className="mt-16 font-medium text-center">{t("setupTwoFactor")}<b/></h1>
                            <div className="flex flex-col justify-center items-center pt-8 pb-4">
                                    <TextField
                                        autoFocus={true}
                                        name="phone"
                                        type="tel"
                                        autoComplete="tel"
                                        disabled={fetching}
                                        placeholder={t("enterYourPhoneNumber")}
                                        onChange={e => setPhone(e.target.value)}
                                        value={phone}
                                        required
                                        error={error}
                                        hideSubmit={false}
                                    />
                                </div>
                        </form>
                    )}
                    {view === "confirm-2fa" && (
                        <ConfirmPhoneNumberView
                            t={t}
                            onSubmit={onConfirm2FASubmit}
                            onSendCode={verifyPhoneNumber}
                            onAccountSwitchClick={logout}
                            disabled={fetching || !recaptchaWidgetId}
                            error={error}
                        />
                    )}
                </div>
            </RegisterContent>
            <RegisterFooter/>
        </div>
    )
}

function ConfirmPhoneNumberView({onSubmit, onSendCode, onAccountSwitchClick, error, disabled}) {
    const {t} = useTranslation("register");
    const [code, setCode] = useState('')
    const [timeout, setTimeout] = useState(-1);

    useEffect(() => {
        setTimeout(60)
    }, [])

    useInterval(() => {
        setTimeout(timeout => timeout - 1);
    }, timeout > -1 ? 1000 : null);

    useEffect(() => {
        if (timeout === 60) {
            //setFetching(true);
            onSendCode && onSendCode()
        }
    }, [timeout]);

    function _onSubmit(e) {
        e.preventDefault()
        onSubmit && onSubmit(code)
    }

    return (
        <form onSubmit={_onSubmit}>
            <div className="Register-Content-Header">
                <h1 className="Register-Heading"><Trans
                    i18nKey="register:securityVerification"
                    components={{
                        b: <b/>
                    }}
                /></h1>
                <div className="Register-Content-Body">
                    <TextField
                        autoFocus={true}
                        name="code"
                        autoComplete="code"
                        disabled={disabled}
                        placeholder={t("enter6digitOneTimeSmsCode")}
                        onChange={e => setCode(e.target.value)}
                        value={code}
                        required
                        error={error}
                        hideSubmit={false}
                    />
                    <div className="Register-Row"/>
                    <button type="button"
                            onClick={() => timeout <= 0 ? setTimeout(60) : ''}
                            className="Button Button--Light"
                            disabled={timeout > 0 || disabled}>
                        {t('sendSmsCode')}
                        {timeout > -1 ? <div style={{margin: "0px 4px 0px 8px"}}>
                            <div style={{
                                opacity: 0.6,
                                display: "inline-flex",
                                justifyContent: "center",
                                width: "12px",
                                height: "12px",
                                textAlign: "center",
                                lineHeight: "8px",
                            }}>{timeout}</div>
                        </div> : ""}
                    </button>
                    <div className="Register-Row"/>
                    <button type="button" onClick={onAccountSwitchClick} className="Button">
                        {t('switchAccount')}
                    </button>
                </div>
            </div>
        </form>
    )
}

export function Login() {
    const {t} = useTranslation("register");
    const navigate = useNavigate();
    const [error, setError] = useState(null);
    const [view, setView] = useState("login") // "login" | "confirm-2fa"
    const [fetching, setFetching] = useState(false);
    const [multiFactorResolver, setMultiFactorResolver] = useState(null);
    const [verificationId, setVerificationId] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passVisible, setPassVisible] = useState(false)
    const [hasVerified, setHasVerified] = useState(false);

    useEffect(() => {
        switch (view) {
            case "login":
                initRecaptcha()
                break;
            case "confirm-2fa":
                //initRecaptcha()
                break;
            default:
                setView("login")
        }
    }, [view])

    async function verifyPhoneNumber() {
        if (!(multiFactorResolver?.hints && multiFactorResolver?.hints[0])) {
            setView("login")
            return
        }

        function _callback() {
            if (hasVerified) return
            if (!multiFactorResolver.hints[0] || !multiFactorResolver.session) return
            const phoneInfoOptions = {
                multiFactorHint: multiFactorResolver.hints[0],
                session: multiFactorResolver.session
            }

            setError(null)
            setHasVerified(true)

            if (!window._phoneAuthProvider) window._phoneAuthProvider = new PhoneAuthProvider(auth);
            return window._phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier).then(verificationId => {
                setFetching(false)
                setVerificationId(verificationId)
                setHasVerified(true)
                return verificationId
            }).catch(err => {
                console.error("verifyPhoneNumber error:", err)
                setFetching(false)
                setError(err.message)
                setHasVerified(false)
            })
        }

        return _callback()
    }

    function logout() {
        signOut(auth).finally(() => window.location.reload())
    }

    function onLoginSubmit(e) {
        e.preventDefault();

        setFetching(true)

        signInWithEmailAndPassword(auth, email, password)
            .then(userRec => {
                const user = userRec.user

                setFetching(false)
                setError(null)

                if (user?.emailVerified && multiFactor(user).enrolledFactors?.length > 0) {
                    setView("confirm-2fa")
                } else if (user?.emailVerified && multiFactor(user).enrolledFactors?.length === 0) {
                    navigate("/enroll")
                } else if (!user) {
                    setError(t('thereWasAnErrorProcessingYourRequestPleaseRefreshT'))
                } else if (!user?.emailVerified) {
                    navigate("/verify-email")
                }
            })
            .catch(err => {
                let error;
                switch (err.code) {
                    case "auth/multi-factor-auth-required":
                        setMultiFactorResolver(getMultiFactorResolver(auth, err))
                        setView("confirm-2fa")
                        break;
                    case "auth/user-disabled":
                        error = t('thisUserAccountHasBeenDisabled')
                        break
                    case "auth/wrong-password":
                        error = t('thePasswordIsInvalidOrTheUserDoesNotExist')
                        break
                    default:
                        console.error(err)
                        error = err.message
                }
                setFetching(false)
                setError(error)
            });
    }

    async function onConfirm2FASubmit(code) {
        if (!verificationId) {
            setError(t('somethingWentWrongPleaseRefreshThePageAndTryAgain'))
            return
        }

        // Ask user for the verification code. Then:
        const cred = PhoneAuthProvider.credential(verificationId, code),
            multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);

        setFetching(true)

        // Complete sign-in. This will also trigger the Auth state listeners.
        multiFactorResolver.resolveSignIn(multiFactorAssertion)
            .then(userCredential => {
                setFetching(false)
                setError(null)
            })
            .catch(err => {
                setFetching(false)
                console.error("onConfirm2FASubmit", err)
                if (err?.message) {
                    setError(err.message)
                } else {
                    setError(t('somethingWentWrongPleaseRefreshThePageAndTryAgain'))
                }
            })
    }

    return (
        <div className="h-full flex flex-col">
            <RegisterHeader/>
            <RegisterContent>
                <div className="m-auto px-8 sm:px-16 md:px-0">
                    {view === "login" && (
                        <>
                            <form onSubmit={onLoginSubmit}>
                                <h1 className="mt-16 font-medium text-center">{t('logInToYourAccount')}<b/></h1>
                                <div className="flex flex-col justify-center items-center pt-8 pb-4">
                                    <TextField
                                        autoFocus={false}
                                        name="username"
                                        type="email"
                                        autoComplete="email"
                                        placeholder={t("usernamePlaceholder")}
                                        onChange={e => setEmail(e.target.value)}
                                        value={email}
                                        disabled={fetching}
                                        fetching={fetching}
                                        required
                                        hideSubmit={true}
                                    />
                                    <TextField
                                        autoFocus={false}
                                        name="password"
                                        type={passVisible ? "text" : "password"}
                                        autoComplete="current-password"
                                        placeholder={t("passwordPlaceholder")}
                                        error={error}
                                        value={password}
                                        disabled={fetching}
                                        fetching={fetching}
                                        onChange={e => setPassword(e.target.value)}
                                        required
                                    />
                                    <Trans i18nKey='register:resetPasswordHere'>
                                        <Link className="text-xs" to={"/lost-password"}>Did
                                            you
                                            forget
                                            your
                                            password?</Link>
                                    </Trans>
                                </div>
                            </form>
                            <RegisterInstead/>
                        </>
                    )}
                    {view === "confirm-2fa" && (
                        <ConfirmPhoneNumberView
                            t={t}
                            onSubmit={onConfirm2FASubmit}
                            onSendCode={verifyPhoneNumber}
                            onAccountSwitchClick={logout}
                            disabled={fetching || !recaptchaWidgetId}
                            error={error}
                        />
                    )}
                </div>
            </RegisterContent>
            <RegisterFooter/>
        </div>
    )
}
